/* ManageOrgsRenderer.js */

"use strict";
const _ = require("lodash");
const CreateOrgComponent = require("./createorg/CreateOrgComponent");
const EditOrgComponent = require("./editorg/EditOrgComponent");
const SingleCombobox = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;

const ORG_CREATED_NO_PACKAGES =
    "Organization created. Use Solutions Manager to install packages. The browser will be refreshed.";
const ORG_CREATED_WITH_PACKAGES = "Organization created. The browser will be refreshed.";
const ORG_UPDATED =
    "You have made changes to the current organization’s profile. The browser will be refreshed.";
const PW_WARNING =
    "You have saved password minimum requirements which exceed the total minimum password length. <br><br>";
const ORG_DELETED = "Organization deleted. The browser will be refreshed.";
const LOAD_PACKAGES_CONFIRMATION = (name) =>
    `Load the Gluware solution packages into the ${name} organization?`;
const DELETE_CONFIRMATION = (name) => `Delete organization named ${name}?`;

class ManageOrgsRenderer {
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("manage-orgs-gl");
        this._rootElem.component = this._model;

        this._headerElem = document.createElement("h1");
        this._headerElem.innerText = "Add/Select Organizations";
        this._headerElem.title = "Select an organization to modify or click Add Organization+";
        this._rootElem.appendChild(this._headerElem);

        this._orgSelector = new SingleCombobox({
            darkTheme: true,
            autoSelect: false,
        });
        this._rootElem.appendChild(this._orgSelector.renderer.getRootElement());

        this._createOrgElem = document.createElement("a");
        this._createOrgElem.innerText = "Add Organization+";
        this._createOrgElem.classList.add("create-option");
        this._rootElem.appendChild(this._createOrgElem);

        this._currentScreenContainer = document.createElement("currentscreen-gl");
        this._rootElem.appendChild(this._currentScreenContainer);

        this._currentScreenComponent = null;

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        this._unregisterListeners();
        this._uiUtils.clearMessage();

        let promArr = [];
        promArr.push(this._clearCurrentScreen());
        promArr.push(this._orgSelector.shutdown());

        return Promise.all(promArr).then(() => {
            this._currentScreenComponent = undefined;
            this._currentScreenContainer.remove();
            this._model = undefined;
            this._uiUtils = undefined;
            this._createOrgElem.remove();
            this._createOrgElem = undefined;
            this._headerElem.remove();
            this._headerElem = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
        });
    }

    //prettier-ignore
    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on("ModelSelectedOrgInfoChange", (this._modelSelectedOrgInfoChange = this._modelSelectedOrgInfoChange.bind(this)));
        this._model.on("CreateSuccess", (this._createSuccessHandler = this._createSuccessHandler.bind(this)));
        this._model.on("CreateError", (this._createErrorHandler = this._createErrorHandler.bind(this)));
        this._model.on("SaveSuccess", (this._saveSuccessHandler = this._saveSuccessHandler.bind(this)));
        this._model.on("SaveError", (this._saveErrorHandler = this._saveErrorHandler.bind(this)));
        this._model.on("DeleteSuccess", (this._deleteSuccessHandler = this._deleteSuccessHandler.bind(this)));
        this._model.on("DeleteError", (this._deleteErrorHandler = this._deleteErrorHandler.bind(this)));
        this._model.on("testLdapResult", (this._onTestLdapResult = this._onTestLdapResult.bind(this)));
        this._model.on("TestLdapError", (this._onTestLdapError = this._onTestLdapError.bind(this)));
        this._model.on("testRadiusResult", (this._onTestRadiusResult = this._onTestRadiusResult.bind(this)));
        this._model.on("TestRadiusError", (this._onTestRadiusError = this._onTestRadiusError.bind(this)));
        this._model.on("LoadPackagesSuccess", (this._onLoadPackagesSuccess = this._onLoadPackagesSuccess.bind(this)));
        this._model.on("LoadPackagesProgress", (this._onLoadPackagesProgress = this._onLoadPackagesProgress.bind(this)));
        this._model.on("LoadPackagesFailure", (this._onLoadPackagesFailure = this._onLoadPackagesFailure.bind(this)));
        this._createOrgElem.addEventListener("click", (this._callCreateOrgScreen = this._callCreateOrgScreen.bind(this)));
        this._orgSelector.on("UserValue", (this._orgSelectionChange = this._orgSelectionChange.bind(this)));
    }

    _unregisterListeners() {
        this._model
            .removeAllListeners("Shutdown")
            .removeAllListeners("ModelSelectedOrgInfoChange")
            .removeAllListeners("CreateSuccess")
            .removeAllListeners("CreateError")
            .removeAllListeners("SaveSuccess")
            .removeAllListeners("SaveError")
            .removeAllListeners("DeleteSuccess")
            .removeAllListeners("DeleteError")
            .removeAllListeners("LoadPackagesSuccess")
            .removeAllListeners("LoadPackagesProgress")
            .removeAllListeners("LoadPackagesFailure");
        this._createOrgElem.removeEventListener("click", this._callCreateOrgScreen);
        this._orgSelector.removeAllListeners("UserValue");
    }
    //prettier-ignore

    _registerScreenListeners() {
        this._currentScreenComponent.on("SwitchContent", (this._switchContent = this._switchContent.bind(this)));
        this._currentScreenComponent.on("CreateOrg", (this._createOrg = this._createOrg.bind(this)));
        this._currentScreenComponent.on("DeleteSelectedOrg", (this._deleteSelectedOrg = this._deleteSelectedOrg.bind(this)));
        this._currentScreenComponent.on("SaveSelectedOrg", (this._saveSelectedOrg = this._saveSelectedOrg.bind(this)));
        this._currentScreenComponent.on("testLDAP", (this._onTestLdap = this._onTestLdap.bind(this)));
        this._currentScreenComponent.on("testRADIUS", (this._onTestRadius = this._onTestRadius.bind(this)));
    }

    _unregisterScreenListeners() {
        this._currentScreenComponent.removeAllListeners();
    }

    _createSuccessHandler(createdOrg) {
        this._uiUtils.writeMessage("Organization Created.");
        this._showLoadPackagesConfirmation()
            .then(
                () => {
                    this._uiUtils.writeMessage("Loading Packages...");
                    this.model.modalUserFeedback.displayLoading({
                        message: "Loading Gluware Solution packages... This could take a moment",
                    });
                    return this.model.loadPackages(createdOrg.id);
                },
                () =>
                    setTimeout(this._pageReloadWithNotification.bind(this, ORG_CREATED_NO_PACKAGES))
            )
            .then(() => (this.model.screenDisplayType = "edit"));
    }

    _createErrorHandler(err) {
        this._currentScreenComponent.crudError({type: "create", error: err});
    }

    _saveSuccessHandler() {
        if (this.model.isEditedOrgSelected()) {
            return this.model._storageFactory
                .whenStorageIsReady()
                .then(() => {
                    const selectedOrg = this.model.selectedOrgInfo,
                        updatedOrgInfo = {
                            id: selectedOrg.id,
                            name: selectedOrg.name,
                        };
                    let dataStorage = this.model._storageFactory.getRemoteStorageInstance(
                        "system",
                        "core"
                    );

                    this.model._localStorage.setActiveOrganization(updatedOrgInfo);
                    //modelEditor hack
                    if (dataStorage) {
                        dataStorage.setData(updatedOrgInfo, "activeOrganization");
                    }
                })
                .then(() => {
                    if (this.model._pwLengthWarning) {
                        return this._pageReloadWithNotification(PW_WARNING + ORG_UPDATED);
                    }
                    return this._pageReloadWithNotification(ORG_UPDATED);
                })
                .catch((err) =>
                    this.model._logger.warn(
                        {error: err},
                        "ManageOrgsRenderer._saveSuccessHandler() Error setting org to the storage"
                    )
                );
        } else {
            if (this.model._pwLengthWarning) {
                this._pageReloadWithNotification(PW_WARNING + ORG_UPDATED);
            } else {
                this._pageReloadWithNotification(ORG_UPDATED);
            }
        }
    }

    _saveErrorHandler(err) {
        this._currentScreenComponent.crudError({type: "save", error: err});
    }

    _deleteSuccessHandler() {
        this._pageReloadWithNotification(ORG_DELETED);
    }

    _deleteErrorHandler(err) {
        this._currentScreenComponent.crudError({type: "delete", error: err});
    }

    _onTestLdapResult(resultObj) {
        this.model.modalUserFeedback.displayNotification(
            `LDAP Server Connection Successful. Found ${resultObj.userCount} matching user entries.`,
            {notDismissable: true}
        );
    }

    _onTestLdapError(err) {
        this._currentScreenComponent.testLdapError(err);
    }

    _onTestRadiusResult() {
        this.model.modalUserFeedback.displayNotification(`RADIUS Server Connection Successful.`, {
            notDismissable: true,
        });
    }

    _onTestRadiusError(err) {
        this._currentScreenComponent.testRadiusError(err);
    }

    _onLoadPackagesSuccess(/*results*/) {
        this._uiUtils.writeMessage("Packages Loaded.");
        this._pageReloadWithNotification(ORG_CREATED_WITH_PACKAGES);
    }

    _onLoadPackagesProgress(msg) {
        this._uiUtils.writeMessage(msg);
        this.model.modalUserFeedback.showLoadingProgress(msg);
    }

    _onLoadPackagesFailure(err) {
        this.model._logger.warn({error: err}, "ManageOrgsRenderer._onLoadPackagesFailure() Error");
        let error = {
            errorCode: "PACKAGE_LOAD_ERROR",
            errorMsg:
                "An error has occurred while loading the Solution packages.  Please use the Solutions Manager to complete the loading process.",
            errorTitle: "Solutions Package Load Error",
        };
        if (err.error && err.error.errorCode === "NotAuthorized") {
            error = Object.assign({}, error, err.error);
        }
        this.model.modalUserFeedback
            .displayError({error, dontHide: true, notDismissable: true})
            .catch(() => {
                setTimeout(this._pageReloadWithNotification.bind(this, ORG_CREATED_NO_PACKAGES));
            });
    }

    _createOrg(info) {
        this.model.createOrg(info);
    }

    _deleteSelectedOrg() {
        this._uiUtils.clearMessage();
        if (this.model.activeOrgId === this.model.selectedOrgId) {
            this._uiUtils.writeErrorMessage(
                "Unable to delete organization: Organization currently selected in home bar."
            );
            return;
        }
        this.model.modalUserFeedback
            .displayConfirmation(DELETE_CONFIRMATION(this.model.selectedOrgInfo.name), {
                title: "Delete",
                confirmDetails: {
                    text: "Confirm",
                    type: "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
                notDismissable: true,
            })
            .then(() => {
                this._uiUtils.writeMessage("Deleting...");
                this.model.deleteSelectedOrg();
            });
    }

    _saveSelectedOrg(orgInfo, pwInfo) {
        this.model.saveSelectedOrg(orgInfo, pwInfo);
    }

    _onTestLdap(ldapConfig) {
        this.model.testLDAPConnection(ldapConfig);
    }

    _onTestRadius(radiusConfig) {
        this.model.testRADIUSConnection(radiusConfig);
    }

    // ORG SELECTION HANDLING
    _orgSelectionChange(info) {
        this.model.selectedOrgId = info;
    }

    _modelSelectedOrgInfoChange(newOrgInfo, newPWInfo) {
        //For the current screen, set its model selected orgInfo.
        //Credentials first - the selectedOrgInfo setter renders, and the render reads that list.
        this._currentScreenComponent.setCredentialsList(this._model._credentialsList);
        this._currentScreenComponent.selectedOrgInfo = newOrgInfo;
        this._currentScreenComponent.passwordPolicy = newPWInfo;
    }

    //SCREEN HANDLING
    _switchContent(displayMode) {
        this._model.screenDisplayType = displayMode;
    }

    _callCreateOrgScreen() {
        this._model.screenDisplayType = "create";
    }

    _renderEditScreen() {
        return this._clearCurrentScreen().then(() => {
            this._createOrgElem.style.display = "";
            this._orgSelector.renderer.getRootElement().style.display = "";
            this._currentScreenComponent = new EditOrgComponent(
                this.model.selectedOrgInfo,
                this.model._retentionPolicyEntity,
                this.model.userInfo,
                this._uiUtils,
                this.model._logger,
                this.model.modalUserFeedback,
                this._model._samloAuthExists,
                this._model._isDCEnabled,
                this._model._credentialsList,
                this._model._passwordPolicy
            );
            this._registerScreenListeners();
            this._currentScreenContainer.appendChild(
                this._currentScreenComponent.renderer.getRootElement()
            );
            return this._currentScreenComponent.renderer.render();
        });
    }

    _renderCreateScreen() {
        return this._clearCurrentScreen().then(() => {
            this._createOrgElem.style.display = "none";
            this._orgSelector.renderer.getRootElement().style.display = "none";
            this._currentScreenComponent = new CreateOrgComponent(
                this.model.allOrgs,
                this.model.userActiveOrganization,
                this.model.userInfo,
                this._uiUtils,
                this.model._logger,
                this.model.modalUserFeedback,
                this.model._isDCEnabled,
                this._model._credentialsList
            );
            this._registerScreenListeners();
            this._currentScreenContainer.appendChild(
                this._currentScreenComponent.renderer.getRootElement()
            );
            return this._currentScreenComponent.renderer.render();
        });
    }

    _clearCurrentScreen() {
        if (this._currentScreenContainer.firstChild) {
            this._unregisterScreenListeners();

            return this._currentScreenContainer.firstChild.component.shutdown();
        }
        return Promise.resolve();
    }

    _renderOrgSelections() {
        let selectedOrgId = _.get(this, "model.selectedOrgId");
        let selectedOrgOption = null;

        let activeOrgId = _.get(this, "model.userActiveOrganization.id");

        let orgOptions = [];

        let orgIds = _.keys(_.get(this, "model.allOrgs"));

        if (orgIds.length > 0) {
            orgIds.forEach((orgId) => {
                let option = {
                    label: this.model.allOrgs[orgId].name,
                    value: orgId,
                };
                orgOptions.push(option);
                if (orgId === selectedOrgId) {
                    selectedOrgOption = option;
                }
            });
        }
        this._orgSelector.options = _.sortBy(orgOptions, (org) => org.label.toLowerCase());

        if (selectedOrgOption) {
            this._orgSelector.value = selectedOrgOption.value;
            return Promise.resolve();
        } else if (orgOptions.length > 0) {
            this._orgSelector.value = activeOrgId;
            return this.model.setSelectedOrgId(this._orgSelector.value, true);
        } else {
            return Promise.resolve();
        }
    }

    _showLoadPackagesConfirmation() {
        return this.model.modalUserFeedback.displayConfirmation(
            LOAD_PACKAGES_CONFIRMATION(this.model.selectedOrgInfo.name),
            {
                title: "Load Gluware Solutions",
                confirmDetails: {
                    text: "Yes",
                    type: "additive",
                },
                rejectDetails: {
                    text: "No",
                    type: "text",
                },
                dontHide: true,
                notDismissable: true,
            }
        );
    }

    _pageReloadWithNotification(msg) {
        this.model.modalUserFeedback
            .displayNotification(msg, {notDismissable: true})
            .then(() => this.model.router.reload());
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this.model) {
            return this.model
                .whenModelIsReady()
                .then(() => {
                    return this._renderOrgSelections();
                })
                .then(() => {
                    if (this.model) {
                        return this.model.whenOrgModelIsReady().then(() => {
                            switch (this.model.screenDisplayType) {
                                case "edit":
                                    return this._renderEditScreen();
                                //break;
                                case "create":
                                    return this._renderCreateScreen();
                                //break;
                            }
                        });
                    }
                });
        }
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this._shutdown();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}
module.exports = ManageOrgsRenderer;
